<main class="join-shell">
  <section class="join-shell__left">
    <img class="join-shell__logo" src="assets/logo-with-text.svg" alt="Stitch.ai" />
    <div class="join-shell__scroll">
      <div class="join-form">
        @if (resolving()) {
          <p class="live" role="status" aria-live="polite">Loading invitation…</p>
        } @else if (resolveError(); as error) {
          <h1>We couldn’t open this invitation</h1>
          <p class="body" aria-live="assertive">
            @if (error === 'rate-limit') {
              Too many attempts from your network. Please wait a minute and try again.
            } @else if (error === 'network') {
              Check your connection and try again.
            } @else {
              This invitation link isn’t available.
            }
          </p>
          <div class="single-actions">
            <button class="primary" type="button" (click)="goToStitch()">Go to Stitch.ai</button>
          </div>
        } @else if (resolution(); as invitation) {
          @if (invitation.state === SpaceInvitationState.EXPIRED) {
            @if (requestSent()) {
              <h1>Request sent</h1>
              <p class="body">We've let {{ invitation.inviterName }} know. You'll get a new invitation by email.</p>
            } @else {
              <h1>This invitation has expired</h1>
              <p class="body">
                Invitations to {{ invitation.teamTitle }} are valid for 7 days. Ask {{ invitation.inviterName }} to send
                you a new one.
              </p>
              @if (requestError()) {
                <p class="error" role="alert">We couldn’t send your request. Please try again.</p>
              }
              <div class="single-actions">
                <button class="primary" type="button" [disabled]="requestPending()" (click)="requestNewInvitation()">
                  {{ requestPending() ? 'Sending request…' : 'Request a new invitation' }}
                </button>
              </div>
            }
          } @else if (invitation.state === SpaceInvitationState.REVOKED) {
            <h1>This invitation is no longer available</h1>
            <p class="body">
              {{ invitation.inviterName }} cancelled this invitation to {{ invitation.teamTitle }}. Get in touch with
              them if you think this is a mistake.
            </p>
            <div class="single-actions">
              <button class="primary" type="button" (click)="goToStitch()">Go to Stitch.ai</button>
            </div>
          } @else if (invitation.state === SpaceInvitationState.ALREADY_ACCEPTED) {
            <h1>You’re already in this team</h1>
            <p class="body">
              You joined {{ invitation.teamTitle }} on {{ acceptedDate(invitation.acceptedAt) }}. Sign in to pick up
              where you left off.
            </p>
            <div class="single-actions">
              <button class="primary" type="button" (click)="goToStitch()">Sign in to Stitch.ai</button>
            </div>
          } @else if (step() === 'landing') {
            <h1>{{ invitation.inviterName }} invited you to join the {{ invitation.teamTitle }} team</h1>
            <p class="body">
              Join {{ invitation.inviterFirstName }} and your teammates in the {{ invitation.teamTitle }} Team Workspace
              to start Stitching together.
            </p>
            <section class="card-section" aria-labelledby="inviter-label">
              <h2 id="inviter-label">Invited by</h2>
              <div class="person-card">
                <span
                  class="avatar avatar--person"
                  [style.background-color]="inviterAvatarPalette(invitation).background"
                  [style.color]="inviterAvatarPalette(invitation).text"
                  >{{ initials(invitation.inviterName) }}</span
                >
                <span class="card-copy"
                  ><strong>{{ invitation.inviterName }}</strong
                  ><small>{{ invitation.inviterAddress }}</small></span
                >
                <span class="badge">{{ invitation.inviterRole | titlecase }}</span>
              </div>
            </section>
            <hr />
            <section class="card-section" aria-labelledby="team-label">
              <h2 id="team-label">Team Workspace</h2>
              <div class="person-card">
                <span
                  class="avatar avatar--team"
                  [style.background-color]="avatarPalette(invitation.teamId).background"
                  [style.color]="avatarPalette(invitation.teamId).text"
                  >{{ initials(invitation.teamTitle) }}</span
                >
                <span class="card-copy"
                  ><strong>{{ invitation.teamTitle }}</strong
                  ><small>{{ invitation.teamAddress }}</small></span
                >
                <span class="member">{{ invitation.role | titlecase }}</span>
              </div>
            </section>
            @if (invitation.state === SpaceInvitationState.TEAM_FULL) {
              <p id="team-full-status" class="team-full-status" role="status">
                {{ invitation.teamTitle }} has no free seat right now. Ask {{ invitation.inviterFirstName }} to make
                room.
              </p>
            }
            @if (alreadyMemberFailure()) {
              <p id="already-member-status" class="error" role="alert">
                You’re already in this team. Sign in to {{ invitation.teamTitle }}.
              </p>
            }
            <div class="landing-actions">
              <button
                class="primary"
                type="button"
                [attr.aria-describedby]="
                  invitation.state === SpaceInvitationState.TEAM_FULL
                    ? alreadyMemberFailure()
                      ? 'team-full-status already-member-status'
                      : 'team-full-status'
                    : alreadyMemberFailure()
                      ? 'already-member-status'
                      : null
                "
                (click)="beginNewAccount()"
              >
                Join Team Workspace
              </button>
              <a class="sign-in-link" href="/auth/login">Already have an account? <span>Sign in</span></a>
            </div>
          } @else if (step() === 'account') {
            <h1>Create your account</h1>
            <p class="body">
              Your first name, last name, and team email address will be visible to your teammates and anybody you send
              an email to.
            </p>
            <form [formGroup]="detailsForm" (ngSubmit)="continueToPassword()">
              <div class="name-row">
                <label
                  >First name<input
                    formControlName="firstName"
                    placeholder="First name"
                    autocomplete="given-name"
                    aria-describedby="first-name-status"
                    [attr.aria-invalid]="
                      detailsForm.controls.firstName.invalid && detailsForm.controls.firstName.touched
                    "
                  />
                  <span id="first-name-status" class="field-error" aria-live="polite">{{
                    fieldErrors().firstName ||
                      (detailsForm.controls.firstName.touched && detailsForm.controls.firstName.errors?.['required']
                        ? 'Please enter your first name.'
                        : '')
                  }}</span></label
                >
                <label
                  >Last name<input
                    formControlName="lastName"
                    placeholder="Last name"
                    autocomplete="family-name"
                    aria-describedby="last-name-status"
                    [attr.aria-invalid]="detailsForm.controls.lastName.invalid && detailsForm.controls.lastName.touched"
                  />
                  <span id="last-name-status" class="field-error" aria-live="polite">{{
                    fieldErrors().lastName ||
                      (detailsForm.controls.lastName.touched && detailsForm.controls.lastName.errors?.['required']
                        ? 'Please enter your last name.'
                        : '')
                  }}</span></label
                >
              </div>
              @if (submitError()) {
                <p class="error" role="alert">{{ submitError() }}</p>
              }
              <div class="paired-actions">
                <button class="secondary" type="button" (click)="backToLanding()">Back</button>
                <button class="primary" type="submit" [disabled]="detailsForm.invalid">Next</button>
              </div>
            </form>
          } @else if (step() === 'password') {
            <h1>Set up your team account</h1>
            <p class="body">
              Select your personal email to use within your team workspace and keep your account secure with a strong
              password
            </p>
            <form [formGroup]="passwordForm" (ngSubmit)="createAccount()">
              <label class="address-label" for="team-email-local-part"
                >Create your personal email address
                <app-input
                  class="address-control"
                  [class.address-control--error]="
                    availability() === 'taken' || availability() === 'invalid' || availability() === 'network'
                  "
                  inputId="team-email-local-part"
                  ariaLabel="Create your personal email address"
                  ariaDescribedBy="public-address-status"
                  placeholder="Enter email address"
                  autocomplete="username"
                  appearance="sapphire-outline"
                  [noAutoCorrect]="true"
                  size="m"
                  [inputFormControl]="passwordForm.controls.teamEmailLocalPart"
                  [rightTemplate]="addressSuffix"
                  [validationMessages]="fieldValidationMessages('teamEmailLocalPart')"
                />
                <ng-template #addressSuffix>
                  <span class="address-suffix" [class.available]="availability() === 'available'"
                    >&#64;{{ invitation.teamHostname }}</span
                  >
                  @if (availability() === 'available') {
                    <app-icon class="tick" name="check-circle" size="s" aria-hidden="true" />
                  }
                </ng-template>
              </label>
              <p id="public-address-status" class="field-status" aria-live="polite">
                @switch (availability()) {
                  @case ('pending') {
                    Checking address…
                  }
                  @case ('available') {
                    Email address is available
                  }
                }
              </p>
              <div class="name-row password-row">
                <label for="join-password"
                  >Password<app-input
                    inputId="join-password"
                    ariaLabel="Password"
                    autocomplete="new-password"
                    appearance="sapphire-outline"
                    size="m"
                    [password]="true"
                    [inputFormControl]="passwordForm.controls.password"
                    [validationMessages]="fieldValidationMessages('password')"
                  />
                </label>
                <label for="join-password-confirmation"
                  >Confirm password<app-input
                    inputId="join-password-confirmation"
                    ariaLabel="Confirm password"
                    ariaDescribedBy="password-status"
                    autocomplete="new-password"
                    appearance="sapphire-outline"
                    size="m"
                    [password]="true"
                    [inputFormControl]="passwordForm.controls.passwordConfirmation"
                    [validationMessages]="fieldValidationMessages('password')"
                  />
                </label>
              </div>
              <div id="password-status" class="field-status" aria-live="polite">
                @if (
                  passwordForm.controls.passwordConfirmation.value &&
                  !passwordForm.controls.passwordConfirmation.errors?.['notEqual']
                ) {
                  <span>Passwords match</span>
                }
              </div>
              <label class="terms"
                ><input type="checkbox" formControlName="termsAccepted" />
                <span
                  >I accept the <a [href]="termsUrl" target="_blank" rel="noopener noreferrer">terms and conditions</a> of
                  Stitch.ai</span
                ></label
              >
              @if (passwordForm.controls.termsAccepted.touched && passwordForm.controls.termsAccepted.invalid) {
                <p class="terms-error error">
                  {{ fieldErrors().termsAccepted || 'Please accept the terms and conditions' }}
                </p>
              }
              @if (submitError()) {
                <p class="error" role="alert">{{ submitError() }}</p>
              }
              <div class="paired-actions">
                <button class="secondary" type="button" (click)="step.set('account')">Back</button>
                <button
                  class="primary"
                  type="submit"
                  [disabled]="passwordForm.invalid || availability() !== 'available'"
                >
                  Create account
                </button>
              </div>
            </form>
          } @else {
            <div class="loading-screen" role="status" aria-live="polite">
              <h1>We're Stitching everything together.</h1>
              <span class="loading-line"></span>
            </div>
          }
        }
      </div>
    </div>
  </section>
  <aside class="join-shell__art" aria-hidden="true"><img src="assets/login/welcome.svg" alt="" /></aside>
</main>
